import { Item } from '../../models/Item'
import { RecordFactory } from '../../models/RecordFactory'
import { ItemSubType } from '../../types/models'
import { ComponentData } from '../../utils/typing'
import { PocketComponent, PocketComponentProps, usePSComponent } from '../PocketComponent'
import { ClassPage } from './ClassPage'
import { GiftTypePage } from './GiftTypePage'
import { PackageTypePage } from './PackageTypePage'
import { ProductPage } from './ProductPage'
import { ReservationPage } from './ReservationPage'
import { ServicePage } from './ServicePage'
import { SubscriptionPlanPage } from './SubscriptionPlanPage'

type Props = {
    itemType: ItemSubType
    item: Item
    enableAllFields?: boolean
    onUpdateItem: (item: Item, file?: File) => void
    onDelete?: () => void
    onClose: () => void
}

const viewWillAppear = async (base: PocketComponent<Props>) => {
    if (
        ['subscription', 'service', 'class', 'reservation', 'package', 'gift'].includes(
            base.props.item.type ?? ''
        )
    ) {
        const [videoConferenceOptions, componentItems] = await Promise.all([
            Item.videoConferenceStrings(base.props.item.type === 'class'),
            RecordFactory.getInstance().loadItems(base.owner.id!, [
                'service',
                'class',
                'reservation',
            ]),
        ])
        return { videoConferenceOptions, componentItems }
    }
    return {
        videoConferenceOptions: [],
        componentItems: [],
    }
}

const render = ({
    data,
    base: {
        owner,
        props: { item, onUpdateItem, onClose, itemType, onDelete, enableAllFields },
    },
}: PocketComponentProps<ComponentData<typeof viewWillAppear>>) => {
    switch (itemType) {
        case 'package':
            return (
                <PackageTypePage
                    defaultPackageItem={item}
                    isLoading={false}
                    onSave={onUpdateItem}
                    owner={owner}
                    onDelete={onDelete}
                    componentItems={data.componentItems}
                    enableAllFields={enableAllFields}
                    onClose={onClose}
                />
            )
        case 'product':
            return (
                <ProductPage
                    item={item}
                    onSave={onUpdateItem}
                    onClose={onClose}
                    onDelete={onDelete}
                    enableAllFields={enableAllFields}
                />
            )
        case 'subscription':
            return (
                <SubscriptionPlanPage
                    item={item}
                    onClose={onClose}
                    onSave={onUpdateItem}
                    onDelete={onDelete}
                    componentItems={data.componentItems}
                    enableAllFields={enableAllFields}
                />
            )
        case 'class':
            return (
                <ClassPage
                    item={item}
                    videoConferenceOptions={data.videoConferenceOptions}
                    onSave={onUpdateItem}
                    onClose={onClose}
                    onDelete={onDelete}
                    enableAllFields={enableAllFields}
                />
            )
        case 'gift':
            return (
                <GiftTypePage
                    item={item}
                    onSave={onUpdateItem}
                    onClose={onClose}
                    onDelete={onDelete}
                    enableAllFields={enableAllFields}
                />
            )
        case 'reservation':
            return (
                <ReservationPage
                    item={item}
                    onSave={onUpdateItem}
                    onClose={onClose}
                    onDelete={onDelete}
                    enableAllFields={enableAllFields}
                />
            )
        default:
            return (
                <ServicePage
                    item={item}
                    onSave={onUpdateItem}
                    onClose={onClose}
                    enableAllFields={enableAllFields}
                    onDelete={onDelete}
                />
            )
    }
}

export const ItemPage = usePSComponent<Props>(render, viewWillAppear)
